/**
 * Payload schemas for the forwarded events that had none (harness to nine B5c, P1).
 *
 * Each schema is written from what its producers send TODAY: every emit site was read and is named
 * on the schema, and each has a wire sample (src/test-utils/wire-samples.ts) that the tests parse.
 * They only DESCRIBE: the relay's forward gate (apps/server relay/forward-parse.ts, FORWARD_PARSE_V1)
 * decides whether a mismatch is observed or dropped, and it forwards the ORIGINAL object either way,
 * so a newer producer's extra field still reaches the browser.
 *
 * Presence follows the producers, types follow their declarations: a field some emit site leaves out
 * is optional; a field typed `string` at the emitter is `z.string()` here even when today's callers
 * pass a few known values (they are noted). A payload that carries a whole stored row (an agent or a
 * routine, read from the database with unvalidated casts) pins the identity its consumers rely on and
 * passes the rest through, as the registry's other row-carrying payloads do.
 *
 * Who authors each frame:
 *  - RUNNER (apps/sandbox, packages/agent-sdk): these reach the forward gate and are parsed there.
 *  - SERVER-DIRECT (apps/server via emitToUser / io.emit): these never pass the gate; the schema is
 *    the written contract, and the gate parses only a frame a runner forged under that name.
 */
import { z } from 'zod';
import { ItemTypeSchema, RoutineConfirmationBlockSchema, RoutineTriggerTypeSchema } from './content-blocks.schema.js';

// ============================================================================
// Session and sandbox (runner, and the server's own idle emits)
// ============================================================================

/**
 * agent:session_state_changed. Runner: stream/session-support.ts emitSessionStateChanged (every
 * turn start and end), sdk/interaction-gateway.ts (requires_action, beside INTERACTION_REQUIRED),
 * stream/harness-sink.ts (two dormant branches; one adds subagentId). Server-direct, always
 * `{ taskId, state: 'idle' }`: sandbox-relay.service.ts, relay/turn-presence.ts, task-stop.service.ts,
 * handlers/socket/user-message-drive.ts, handlers/socket/gate-refusal.ts.
 */
export const SessionStateChangedPayloadSchema = z.object({
  taskId: z.string(),
  state: z.enum(['running', 'idle', 'requires_action']),
  subagentId: z.string().optional(),
});

/**
 * sandbox:state. Runner: handlers/index.ts on every connection, `state.getSandboxState()`
 * (state/state-manager.ts). No consumer reads it today.
 */
export const SandboxStatePayloadSchema = z.object({
  lifecycle: z.literal('ready'),
  activeTasks: z.array(z.object({ taskId: z.string() })),
  hasPendingQuestions: z.boolean(),
  pendingQuestionsCount: z.number(),
  activeQueriesCount: z.number(),
  activityReasons: z.array(z.string()),
  /** Epoch ms. */
  timestamp: z.number(),
});

// ============================================================================
// In-task cards (runner hooks, and the server's structured-output lane)
// ============================================================================

/**
 * routine:created_in_task: the routine confirmation card's fields plus the task. Runner:
 * hooks/routine-detection-hook.ts (from the server's curl envelope). Server-direct:
 * services/routine-from-structured-output.service.ts. The card is
 * RoutineConfirmationBlockSchema minus its `type`, so wire and card cannot drift apart.
 */
export const RoutineCreatedInTaskPayloadSchema = RoutineConfirmationBlockSchema.omit({ type: true }).extend({
  taskId: z.string(),
});

/**
 * item:created_in_task. Runner: hooks/item-detection-hook.ts, from the server's item_created
 * envelope (handlers/items-routes.handler.ts always fills `name`, falling back to the slug, and
 * omits icon and color when absent: never null). The wire field is `id`; the card's is `itemId`.
 */
export const ItemCreatedInTaskPayloadSchema = z.object({
  taskId: z.string(),
  itemType: ItemTypeSchema,
  id: z.string(),
  slug: z.string(),
  abilitySlug: z.string(),
  name: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
});

/**
 * prompt:suggestion. Runner: agent-sdk engine/post-turn-off-path.ts (a raw harness event, sent as
 * is by stream/harness-sink.ts) after a cleanly completed native turn; never sent empty.
 */
export const PromptSuggestionPayloadSchema = z.object({
  taskId: z.string(),
  suggestion: z.string(),
});

// ============================================================================
// Browser (runner tabs and local panes; server-direct cloud stream)
// ============================================================================

/** One tab of a browser:tabs_updated snapshot (types/browser-stream.ts BrowserTab). */
export const BrowserTabSchema = z.object({
  targetId: z.string(),
  url: z.string(),
  title: z.string(),
  favicon: z.string().nullable(),
  taskId: z.string().nullable(),
  /** Stamped on the local-chromium lane only. */
  active: z.boolean().optional(),
});

/** browser:tabs_updated. Runner: browser/browser-session.ts (debounced, de-duplicated snapshot). */
export const BrowserTabsUpdatedPayloadSchema = z.object({
  tabs: z.array(BrowserTabSchema),
});

/**
 * browser:local_ready: which in-app browser body is live, the union of the three announces typed
 * in types/browser-stream.ts (BrowserLocalReadyEventPayload). Runner: browser/browser-session.ts.
 * `transport` is the tag; the view-server announce carries none.
 */
export const BrowserLocalReadyEventPayloadSchema = z.union([
  z.object({
    sessionId: z.string(),
    transport: z.literal('view-server').optional(),
    wsUrl: z.string(),
    token: z.string(),
    width: z.number(),
    height: z.number(),
  }),
  z.object({ sessionId: z.string(), transport: z.literal('embedded') }),
  z.object({ sessionId: z.string(), transport: z.literal('extension') }),
]);

/** browser:local_stopped. Runner: browser/browser-session.ts (local-chromium and extension teardown). */
export const BrowserLocalStoppedPayloadSchema = z.object({
  sessionId: z.string(),
  reason: z.enum(['closed', 'idle']).optional(),
});

/**
 * browser:stream_ready. Server-direct: relay/browser-session.ts (the neko mint, to the room and to
 * one socket). No web consumer: the browser view machine replaced it.
 */
export const BrowserStreamReadyPayloadSchema = z.object({
  sessionId: z.string(),
  nekoUrl: z.string(),
  previewToken: z.string().optional(),
  /** The query parameter previewToken rides in: `_token` on boat; unset on Blaxel, read as `bl_preview_token`. */
  previewTokenParam: z.string().optional(),
  nekoToken: z.string().optional(),
  iceServers: z
    .array(z.object({ urls: z.array(z.string()), username: z.string().optional(), credential: z.string().optional() }))
    .optional(),
  width: z.number(),
  height: z.number(),
});

/**
 * browser:stream_stopped. Server-direct: sandbox-relay.service.ts onBrowserSessionStopped,
 * relay/browser-session.ts (idle, lease off), handlers/socket/orchestrator.ts (replay). No web
 * consumer.
 */
export const BrowserStreamStoppedPayloadSchema = z.object({
  sessionId: z.string(),
  reason: z.enum(['closed', 'idle']).optional(),
});

// ============================================================================
// OS hands (runner)
// ============================================================================

/**
 * os_hands:active (computer-use/os-hands-protocol.ts OsHandsActivePayload). Runner:
 * os-hands/os-hands-activity.ts (active and inactive, de-duplicated; re-sent on reconnect).
 */
export const OsHandsActivePayloadSchema = z.object({
  taskId: z.string(),
  active: z.boolean(),
  takeover: z.boolean(),
  app: z.string().optional(),
  /** OS_ACTION_RUNGS (computer-use/os-action-report.ts). */
  rung: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
});

// ============================================================================
// Agents (server-direct; consumers refetch)
// ============================================================================

/** A stored agent definition as the server sends it: the slug its consumers key on, the rest through. */
const AgentRowSchema = z.object({ slug: z.string() }).passthrough();

/** agent:installed. Server-direct: handlers/agent-library.handler.ts. */
export const AgentInstalledPayloadSchema = z.object({
  agent: AgentRowSchema,
  source: z.literal('library'),
});

/** agent:created and agent:updated. Server-direct: handlers/agents-routes.handler.ts. */
export const AgentCrudPayloadSchema = z.object({
  agent: AgentRowSchema,
  source: z.literal('crud'),
});

/**
 * agent:uninstalled, agent:published, agent:unpublished (handlers/agent-library.handler.ts) and
 * agent:deleted (handlers/agents-routes.handler.ts). Server-direct.
 */
export const AgentSlugPayloadSchema = z.object({
  slug: z.string(),
});

// ============================================================================
// Workspace and files (server-direct)
// ============================================================================

/**
 * workspace:sync:started. Server-direct: sandbox-relay.service.ts emitWorkspaceSyncStarted, from
 * sandbox/volume-delivery.ts (today 'sandbox_create' or 'sandbox_wake').
 */
export const WorkspaceSyncStartedPayloadSchema = z.object({
  userId: z.string(),
  reason: z.string(),
});

/** workspace:sync:completed. Server-direct: sandbox-relay.service.ts emitWorkspaceSyncCompleted. */
export const WorkspaceSyncCompletedPayloadSchema = z.object({
  userId: z.string(),
  added: z.number(),
  updated: z.number(),
  deleted: z.number().optional(),
});

/**
 * files:file_synced. Server-direct: sandbox-relay.service.ts emitUserFileSynced, from the file
 * watcher, handlers/file-routes.handler.ts (uploads) and creation-thumb-queue.service.ts.
 */
export const UserFileSyncedPayloadSchema = z.object({
  userId: z.string(),
  filename: z.string(),
  source: z.enum(['user', 'agent']),
  taskId: z.string().optional(),
});

/**
 * files:file_ready (types/files.ts UserFileReadyPayload). Server-direct: sandbox-relay.service.ts
 * registerUserFileAsTaskOutput and relay-image-preview.ts. `cdnUrl` is '' when the image rides
 * inline as `base64`.
 */
export const UserFileReadyPayloadSchema = z.object({
  taskId: z.string(),
  filePath: z.string(),
  filename: z.string(),
  cdnUrl: z.string(),
  mediaType: z.string(),
  base64: z.string().optional(),
});

// ============================================================================
// Routines (server-direct; consumers refetch or read routineId)
// ============================================================================

/** A stored routine as the server sends it: the id its consumers key on, the rest through. */
const RoutineRowSchema = z.object({ id: z.string() }).passthrough();

/**
 * routine:created. Server-direct: handlers/routine-routes.handler.ts and
 * composer/catalog/vestra/routines-edit.ts.
 */
export const RoutineCreatedPayloadSchema = z.object({
  routine: RoutineRowSchema,
});

/**
 * routine:updated, two shapes: the edited row (handlers/routine-routes.handler.ts,
 * composer/catalog/vestra/routines.ts and routines-edit.ts), or just its id after a hold or an arm
 * transition (routine-hold.service.ts, routine-arm.service.ts).
 */
export const RoutineUpdatedPayloadSchema = z.union([
  z.object({ routine: RoutineRowSchema }),
  z.object({ routineId: z.string() }),
]);

/**
 * routine:deleted. Server-direct: handlers/routine-routes.handler.ts and
 * composer/catalog/vestra/routines.ts. The key is `id`, not `routineId`.
 */
export const RoutineDeletedPayloadSchema = z.object({
  id: z.string(),
});

/** routine:run:started. Server-direct: routine-executor.service.ts. `timestamp` is ISO. */
export const RoutineRunStartedPayloadSchema = z.object({
  routineId: z.string(),
  runId: z.string(),
  triggerType: RoutineTriggerTypeSchema,
  timestamp: z.string(),
});

/**
 * routine:run:completed (sandbox-relay.service.ts, a served turn) and routine:completed
 * (routine-executor.service.ts, a one-off routine after dispatch). Server-direct.
 */
export const RoutineRunCompletedPayloadSchema = z.object({
  routineId: z.string(),
  runId: z.string(),
  timestamp: z.string(),
});

/**
 * routine:run:failed. Server-direct: routine-executor.service.ts (a skipped fire, a lost dispatch),
 * sandbox-relay.service.ts (an agent error, no served turn), routine-hold.service.ts,
 * handlers/socket/gate-refusal.ts.
 */
export const RoutineRunFailedPayloadSchema = z.object({
  routineId: z.string(),
  runId: z.string(),
  error: z.string(),
  timestamp: z.string(),
});

/** routine:register_failed. Server-direct: routine-from-structured-output.service.ts. */
export const RoutineRegisterFailedPayloadSchema = z.object({
  taskId: z.string(),
  error: z.string(),
  name: z.string().optional(),
});
